import { useState, useCallback, useRef } from 'react';
import { useFormContext } from 'react-hook-form';
import { FormValues } from '@/components/checkout/CheckoutForm';
import { cepService } from '@/services/cepService';

export const useCepSearch = () => {
  const [isLoadingCep, setIsLoadingCep] = useState(false);
  const [hasManualChanges, setHasManualChanges] = useState({
    street: false,
    neighborhood: false,
    city: false,
    state: false,
  });
  const { setValue, getValues } = useFormContext<FormValues>();
  const lastSearchedCep = useRef<string>('');

  const searchCep = useCallback(async (cep: string) => {
    if (!cepService.isValidCep(cep)) {
      return;
    }
    const normalizedCep = cep.replace(/\D/g, '');
    if (lastSearchedCep.current === normalizedCep) {
      return;
    }
    lastSearchedCep.current = normalizedCep;
    setIsLoadingCep(true);
    try {
      const addressData = await cepService.searchCep(cep);
      if (addressData) {
        const currentValues = {
          street: getValues('street'),
          neighborhood: getValues('neighborhood'),
          city: getValues('city'),
          state: getValues('state'),
          number: getValues('number'),
          complement: getValues('complement'),
        };


        if (!hasManualChanges.street && !currentValues.street) {
          setValue('street', addressData.street || '', {
            shouldValidate: false,
          });
        }
        if (!hasManualChanges.neighborhood && !currentValues.neighborhood) {
          setValue('neighborhood', addressData.neighborhood || '', {
            shouldValidate: false,
          });
        }
        if (!hasManualChanges.city && !currentValues.city) {
          setValue('city', addressData.city || '', {
            shouldValidate: false,
          });
        }
        if (!hasManualChanges.state && !currentValues.state) {
          setValue('state', addressData.state || '', {
            shouldValidate: false,
          });
        }
        setValue('number', currentValues.number || '', {
          shouldValidate: false,
        });
        setValue('complement', currentValues.complement || '', {
          shouldValidate: false,
        });
      }
    } catch (error) {
      console.error('Erro ao buscar CEP:', error);
    } finally {
      setIsLoadingCep(false);
    }
  }, [setValue, getValues, hasManualChanges]);

  const handleManualChange = useCallback((field: keyof typeof hasManualChanges) => {
    setHasManualChanges((prev) => ({
      ...prev,
      [field]: true,
    }));
  }, []);

  const resetManualChanges = useCallback(() => {
    setHasManualChanges({
      street: false,
      neighborhood: false,
      city: false,
      state: false,
    });
  }, []);

  return {
    searchCep,
    isLoadingCep,
    handleManualChange,
    resetManualChanges
  };
};
